import { ConstantScope } from "./shared-enums/constant-scope.enum";

/**
 * expression alias prefixes for constant operands, the prefixed namespace is reserved for constants
 *
 * tokens take the full operand shape like $SYS_chlorine_cost[1].value
 */
export const ORG_CONSTANT_ALIAS_PREFIX = "ORG_";
export const SYS_CONSTANT_ALIAS_PREFIX = "SYS_";

const prefixForScope = (scope: ConstantScope) =>
  scope === ConstantScope.Organization ? ORG_CONSTANT_ALIAS_PREFIX : SYS_CONSTANT_ALIAS_PREFIX;

/**
 * every alias spelling the calc alias map registers for a constant, with and without the dollar sign
 */
export const constantAliasSpellings = (scope: ConstantScope, keyname: string): string[] => {
  const base = `${prefixForScope(scope)}${keyname}`;
  return [base, `$${base}`];
};

export type ParsedConstantAlias = {
  keyname: string;
  scope: ConstantScope;
};

/**
 * parses an expression alias into scope and keyname, null when the alias is not a constant token
 */
export const parseConstantAlias = (alias: string): ParsedConstantAlias | null => {
  const withoutDollar = alias.startsWith("$") ? alias.slice(1) : alias;

  if (withoutDollar.startsWith(ORG_CONSTANT_ALIAS_PREFIX)) {
    const keyname = withoutDollar.slice(ORG_CONSTANT_ALIAS_PREFIX.length);
    if (keyname.length === 0) {
      return null;
    }
    return { keyname, scope: ConstantScope.Organization };
  }

  if (withoutDollar.startsWith(SYS_CONSTANT_ALIAS_PREFIX)) {
    const keyname = withoutDollar.slice(SYS_CONSTANT_ALIAS_PREFIX.length);
    if (keyname.length === 0) {
      return null;
    }
    return { keyname, scope: ConstantScope.System };
  }

  return null;
};

export const isConstantAlias = (alias: string): boolean => parseConstantAlias(alias) !== null;

/**
 * cheap guard so calc runs skip the constants lookup when an expression cannot contain constant tokens
 *
 * substring match, so a metric keyname containing a prefix causes a harmless extra lookup, never a miss
 */
export const expressionMayReferenceConstants = (expression: string | null | undefined): boolean => {
  if (!expression) {
    return false;
  }
  return expression.includes(ORG_CONSTANT_ALIAS_PREFIX) || expression.includes(SYS_CONSTANT_ALIAS_PREFIX);
};

/**
 * the dollar prefixed alias for a constant, the operand without the ceremonial specifier
 */
export const constantAliasToken = (scope: ConstantScope, keyname: string): string =>
  `$${prefixForScope(scope)}${keyname}`;

/**
 * the full expression operand for a constant, what authors paste into expressions
 */
export const constantOperandToken = (scope: ConstantScope, keyname: string): string =>
  `${constantAliasToken(scope, keyname)}[1].value`;
